// Module Mon Score - Dashboard d'ouverture Candidat
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useSession } from '@/contexts/SessionContext';
import { useNavigate } from 'react-router-dom';
import { CandidateServices } from '@/services/CandidateServices';
import { Candidate } from '@/models';
import { ProfileType } from '@/types';
import {
  FiTrendingUp, FiAlertCircle, FiCheckCircle, FiClock,
  FiFileText, FiUser, FiDollarSign, FiHome, FiArrowRight,
  FiChevronRight, FiBriefcase, FiUpload, FiBarChart2, FiDownload,
  FiUsers, FiHeart, FiShield
} from 'react-icons/fi';

// Types
interface ScoreCriteria {
  name: string;
  score: number;
  maxScore: number;
  status: 'excellent' | 'good' | 'average' | 'poor';
  recommendations: string[];
}

interface NextAction {
  id: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  impact: string;
  icon: React.ReactNode;
  link: string;
}

// Styled Components
const WelcomeSection = styled.div`
  background: ${theme.colors.primary};
  color: white;
  padding: ${theme.spacing['2xl']};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.lg};
`;

const WelcomeTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const WelcomeSubtitle = styled.p`
  font-size: ${theme.typography.sizes.lg};
  margin: 0;
  opacity: 0.9;
`;

const ScoreOverview = styled.div`
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ScoreCard = styled.div`
  background: white;
  padding: ${theme.spacing['2xl']};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.colors.primary};
  }
`;

const ScoreValue = styled.div<{ score: number }>`
  font-size: 72px;
  font-weight: ${theme.typography.weights.bold};
  color: ${props => props.score >= 70 ? theme.colors.success :
      props.score >= 50 ? theme.colors.warning : theme.colors.error};
  margin-bottom: ${theme.spacing.sm};
`;

const ScoreLabel = styled.div`
  font-size: ${theme.typography.sizes.lg};
  color: ${theme.colors.text}80;
  margin-bottom: ${theme.spacing.lg};
`;

const ScoreProgress = styled.div`
  width: 100%;
  height: 12px;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  margin-bottom: ${theme.spacing.lg};
`;

const ScoreProgressFill = styled.div<{ score: number }>`
  height: 100%;
  width: ${props => props.score}%;
  background: ${theme.colors.primary};
  transition: width 1s ease;
`;

const ComparisonText = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  padding: ${theme.spacing.md};
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  margin-top: ${theme.spacing.md};
`;

const ViewScoringSheetButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary}DD;
    transform: translateY(-2px);
  }
`;

const NextStepCard = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const CardTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const NextActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const ActionItem = styled.div<{ priority: string }>`
  display: flex;
  align-items: start;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  border-left: 4px solid ${props => {
    switch (props.priority) {
      case 'high': return theme.colors.error;
      case 'medium': return theme.colors.warning;
      default: return theme.colors.info;
    }
  }};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
    transform: translateX(4px);
  }
`;

const ActionIcon = styled.div<{ priority: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  background: ${props => {
    switch (props.priority) {
      case 'high': return theme.colors.error + '20';
      case 'medium': return theme.colors.warning + '20';
      default: return theme.colors.info + '20';
    }
  }};
  color: ${props => {
    switch (props.priority) {
      case 'high': return theme.colors.error;
      case 'medium': return theme.colors.warning;
      default: return theme.colors.info;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
`;

const ActionContent = styled.div`
  flex: 1;
`;

const ActionTitle = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const ActionDescription = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin-bottom: ${theme.spacing.xs};
`;

const ActionImpact = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.success};
  font-weight: ${theme.typography.weights.semibold};
`;

const ActionArrow = styled.div`
  color: ${theme.colors.text}40;
  font-size: 20px;
  flex-shrink: 0;
`;

const CriteriaSection = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.xl};
`;

const CriteriaGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.lg};
`;

const CriteriaItem = styled.div`
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
`;

const CriteriaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
`;

const CriteriaName = styled.h4`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0;
`;

const CriteriaScore = styled.div<{ status: string }>`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${props => {
    switch (props.status) {
      case 'excellent': return theme.colors.success;
      case 'good': return theme.colors.info;
      case 'average': return theme.colors.warning;
      default: return theme.colors.error;
    }
  }};
`;

const CriteriaBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  margin-bottom: ${theme.spacing.md};
`;

const CriteriaBarFill = styled.div<{ percentage: number; status: string }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => {
    switch (props.status) {
      case 'excellent': return theme.colors.success;
      case 'good': return theme.colors.info;
      case 'average': return theme.colors.warning;
      default: return theme.colors.error;
    }
  }};
  transition: width 1s ease;
`;

const RecommendationsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const RecommendationItem = styled.li`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  padding: ${theme.spacing.xs} 0;
  display: flex;
  align-items: start;
  gap: ${theme.spacing.sm};

  &::before {
    content: '→';
    color: ${theme.colors.primary};
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const HouseholdInfoCard = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.xl};
  border-left: 4px solid ${theme.colors.primary};
`;

const HouseholdHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};

  svg {
    font-size: 28px;
    color: ${theme.colors.primary};
  }

  h3 {
    font-size: ${theme.typography.sizes.xl};
    font-weight: ${theme.typography.weights.semibold};
    color: ${theme.colors.text};
    margin: 0;
  }
`;

const HouseholdGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
`;

const HouseholdItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.md};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
`;

const HouseholdLabel = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}70;
  text-transform: uppercase;
  font-weight: ${theme.typography.weights.medium};
`;

const HouseholdValue = styled.div`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  svg {
    font-size: 20px;
  }
`;

const HouseholdBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.success}20;
  color: ${theme.colors.success};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  margin-top: ${theme.spacing.md};

  svg {
    font-size: 16px;
  }
`;

const ApplicationsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};
`;

const ApplicationCard = styled.div`
  background: white;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  border-left: 4px solid ${theme.colors.primary};
  transition: all ${theme.transitions.fast};
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.lg};
  }
`;

const ApplicationTitle = styled.h4`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const ApplicationStatus = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  background: ${props => {
    switch (props.status) {
      case 'scored': return theme.colors.info + '20';
      case 'verification': return theme.colors.warning + '20';
      default: return theme.colors.surface;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'scored': return theme.colors.info;
      case 'verification': return theme.colors.warning;
      default: return theme.colors.text;
    }
  }};
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary}dd;
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 18px;
  }
`;

// Helper functions to get form sections based on profile type
const getFormSections = (profileType: ProfileType | null, isCandidate: boolean) => {
  if (!profileType) return [];

  const employeeSections = [
    { id: 1, name: 'Informations personnelles', icon: <FiUser />, field: 'personal_info' },
    { id: 2, name: 'Candidature en couple', icon: <FiUsers />, field: 'couple_info' },
    { id: 3, name: 'Situation professionnelle', icon: <FiBriefcase />, field: 'employment_info' },
    { id: 4, name: 'Revenus du foyer', icon: <FiDollarSign />, field: 'income_info' },
    { id: 5, name: 'Situation financière', icon: <FiTrendingUp />, field: 'financial_situation' },
    { id: 6, name: 'Garanties', icon: <FiShield />, field: 'guarantees' },
    { id: 7, name: 'Préférences logement', icon: <FiHome />, field: 'housing_preferences' },
  ];

  const entrepreneurSections = [
    { id: 1, name: 'Informations personnelles', icon: <FiUser />, field: 'personal_info' },
    { id: 2, name: 'Candidature en couple', icon: <FiUsers />, field: 'couple_info' },
    { id: 3, name: 'Informations entreprise', icon: <FiBriefcase />, field: 'business_info' },
    { id: 4, name: 'Finances entreprise', icon: <FiDollarSign />, field: 'business_financials' },
    { id: 5, name: 'Clients et fournisseurs', icon: <FiUsers />, field: 'clients_suppliers' },
    { id: 6, name: 'Garanties et références', icon: <FiShield />, field: 'entrepreneur_guarantees' },
    { id: 7, name: 'Préférences logement', icon: <FiHome />, field: 'housing_preferences' },
  ];

  const informalSections = [
    { id: 1, name: 'Informations personnelles', icon: <FiUser />, field: 'personal_info' },
    { id: 2, name: 'Candidature en couple', icon: <FiUsers />, field: 'couple_info' },
    { id: 3, name: 'Votre activité', icon: <FiBriefcase />, field: 'informal_activity' },
    { id: 4, name: 'Vos revenus', icon: <FiDollarSign />, field: 'informal_income' },
    { id: 5, name: 'Vos clients', icon: <FiUsers />, field: 'informal_clients' },
    { id: 6, name: 'Épargne et tontines', icon: <FiTrendingUp />, field: 'informal_savings' },
    { id: 7, name: 'Vos charges', icon: <FiDollarSign />, field: 'informal_expenses' },
    { id: 8, name: 'Références', icon: <FiShield />, field: 'informal_references' },
    { id: 9, name: 'Preuves d\'activité', icon: <FiFileText />, field: 'informal_proofs' },
    { id: 10, name: 'Préférences logement', icon: <FiHome />, field: 'housing_preferences' },
  ];

  const sections =
    profileType === 'employee' ? employeeSections :
    profileType === 'entrepreneur' ? entrepreneurSections :
    profileType === 'informal' ? informalSections : [];

  if (!isCandidate) {
    sections.push({ id: sections.length + 1, name: 'Candidature logements', icon: <FiHome />, field: 'housing_application' });
  }

  sections.push({ id: sections.length + 1, name: 'Déclarations', icon: <FiCheckCircle />, field: 'declarations' });

  return sections;
};

// Check if a section is complete based on candidate data
const isSectionComplete = (sectionField: string, candidateData: Partial<Candidate>): boolean => {
  switch (sectionField) {
    case 'personal_info':
      return !!(candidateData.civilite && candidateData.date_naissance && candidateData.nationality);
    case 'couple_info':
      return candidateData.candidature_couple !== undefined;
    case 'employment_info':
      return !!((candidateData as any).current_employer && (candidateData as any).job_title);
    case 'income_info':
      return !!(candidateData.monthly_income && candidateData.monthly_income > 0);
    case 'financial_situation':
      return candidateData.total_charges !== undefined && candidateData.total_epargne !== undefined;
    case 'guarantees':
      return true; // Always consider complete if reached
    case 'housing_preferences':
      return !!(candidateData.type_logement_souhaite && candidateData.type_logement_souhaite.length > 0);
    case 'business_info':
      return !!((candidateData as any).company_name && (candidateData as any).business_sector);
    case 'business_financials':
      return !!((candidateData as any).annual_revenue);
    case 'clients_suppliers':
      return true; // Always consider complete if reached
    case 'entrepreneur_guarantees':
      return true; // Always consider complete if reached
    case 'informal_activity':
      return !!((candidateData as any).activity_type && (candidateData as any).activity_description);
    case 'informal_income':
      return !!(candidateData.monthly_income && candidateData.monthly_income > 0);
    case 'informal_clients':
      return true; // Always consider complete if reached
    case 'informal_savings':
      return candidateData.total_epargne !== undefined;
    case 'informal_expenses':
      return candidateData.total_charges !== undefined;
    case 'informal_references':
      return true; // Always consider complete if reached
    case 'informal_proofs':
      return true; // Always consider complete if reached
    case 'housing_application':
      return true; // Check later with applications
    case 'declarations':
      return !!(candidateData.informations_exactes && candidateData.conditions_acceptees);
    default:
      return false;
  }
};

export const CandidateScoreDashboard: React.FC = () => {
  const { loggedUser: user } = useSession();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [candidateData, setCandidateData] = useState<Partial<Candidate> | null>(null);
  const [nextActions, setNextActions] = useState<NextAction[]>([]);

  const globalScore = 72;

  // Load candidate data
  useEffect(() => {
    const loadCandidateData = async () => {
      try {
        setLoading(true);
        const response = await CandidateServices.getUserFolder();
        const result = response as {
          success: boolean;
          data?: {
            candidate: Candidate;
            files: any[];
          };
        };

        if (result.success && result.data) {
          setCandidateData(result.data.candidate);

          // Generate next actions based on incomplete sections
          const isCandidate = user?.role_codes?.includes('candidate') || false;
          const sections = getFormSections(result.data.candidate.profile_type as ProfileType, isCandidate);

          const incompleteActions: NextAction[] = [];
          sections.forEach((section, index) => {
            if (!isSectionComplete(section.field, result.data!.candidate)) {
              const priority = index < 3 ? 'high' : index < 6 ? 'medium' : 'low';
              incompleteActions.push({
                id: section.id.toString(),
                title: `Compléter: ${section.name}`,
                description: `Cette section est requise pour finaliser votre dossier`,
                priority,
                impact: index < 3 ? '+10 points estimés' : index < 6 ? '+5 points estimés' : '+3 points estimés',
                icon: section.icon,
                link: '/mon-dossier'
              });
            }
          });

          // Limit to top 3 next actions
          setNextActions(incompleteActions.slice(0, 3));
        }
      } catch (err) {
        console.error('Error loading candidate data:', err);
        // If no folder exists, suggest starting application
        setNextActions([
          {
            id: '1',
            title: 'Démarrer votre candidature',
            description: 'Commencez à remplir votre dossier de candidature',
            priority: 'high',
            impact: 'Première étape vers votre logement',
            icon: <FiFileText />,
            link: '/mon-dossier'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadCandidateData();
  }, [user]);

  // Household information from candidate data
  const household = candidateData ? {
    withSpouse: candidateData.candidature_couple || false,
    mainApplicant: {
      name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Candidat',
      income: candidateData.monthly_income || 0
    },
    spouse: {
      name: (candidateData as any).spouse_name || 'Conjoint(e)',
      income: candidateData.revenus_mensuels_conjoint_salarie || 0
    },
    dependents: (candidateData.children || 0) + (candidateData.other_dependents || 0),
    totalIncome: (candidateData.monthly_income || 0) + (candidateData.candidature_couple ? (candidateData.revenus_mensuels_conjoint_salarie || 0) : 0),
    householdSize: 1 + (candidateData.candidature_couple ? 1 : 0) + (candidateData.children || 0) + (candidateData.other_dependents || 0),
    get incomePerPerson() {
      return this.householdSize > 0 ? Math.round(this.totalIncome / this.householdSize) : 0;
    }
  } : null;

  const scoreCriteria: ScoreCriteria[] = [
    {
      name: 'Situation financière',
      score: 18,
      maxScore: 25,
      status: 'good',
      recommendations: [
        'Ajoutez vos dernières fiches de paie',
        'Complétez votre déclaration de revenus'
      ]
    },
    {
      name: 'Stabilité professionnelle',
      score: 20,
      maxScore: 25,
      status: 'excellent',
      recommendations: []
    },
    {
      name: 'Documents fournis',
      score: 16,
      maxScore: 20,
      status: 'good',
      recommendations: [
        'Il manque votre avis d\'imposition',
        'Ajoutez un justificatif de domicile récent'
      ]
    },
    {
      name: 'Composition du foyer',
      score: 12,
      maxScore: 15,
      status: 'good',
      recommendations: []
    },
    {
      name: 'Historique locatif',
      score: 6,
      maxScore: 15,
      status: 'average',
      recommendations: [
        'Ajoutez une attestation de votre ancien propriétaire',
        'Fournissez les quittances de loyer des 3 derniers mois'
      ]
    }
  ];

  // nextActions are now loaded dynamically from useEffect

  const applications = [
    {
      id: '1',
      title: 'Appartement T3 - Résidence Les Chênes',
      status: 'scored',
      statusText: 'Scorée - En attente commission'
    },
    {
      id: '2',
      title: 'Studio - Centre-ville',
      status: 'verification',
      statusText: 'Vérification documents'
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'excellent': return <FiCheckCircle />;
      case 'good': return <FiCheckCircle />;
      case 'average': return <FiAlertCircle />;
      default: return <FiAlertCircle />;
    }
  };

  return (
    <DashboardLayout>
      <WelcomeSection>
        <WelcomeTitle>🏠 Bienvenue, {user?.first_name || 'Candidat'} !</WelcomeTitle>
        <WelcomeSubtitle>
          {candidateData
            ? 'Voici votre tableau de bord personnel P-Screen'
            : 'Commencez votre parcours vers votre logement idéal'}
        </WelcomeSubtitle>
      </WelcomeSection>

      {!candidateData && !loading && (
        <div style={{
          background: theme.colors.info + '15',
          padding: theme.spacing.xl,
          borderRadius: theme.borderRadius.lg,
          marginBottom: theme.spacing.xl,
          border: `2px solid ${theme.colors.info}30`,
          textAlign: 'center'
        }}>
          <FiFileText style={{ fontSize: '48px', color: theme.colors.info, marginBottom: theme.spacing.md }} />
          <h3 style={{
            fontSize: theme.typography.sizes.xl,
            fontWeight: theme.typography.weights.semibold,
            color: theme.colors.text,
            margin: `0 0 ${theme.spacing.sm} 0`
          }}>
            Vous n'avez pas encore de dossier de candidature
          </h3>
          <p style={{
            fontSize: theme.typography.sizes.base,
            color: theme.colors.text + '70',
            margin: `0 0 ${theme.spacing.lg} 0`
          }}>
            Commencez dès maintenant à créer votre dossier pour accéder aux logements disponibles
          </p>
          <Button
            {...({$variant: "primary"} as any)}
            onClick={() => navigate('/mon-dossier')}
            style={{ margin: '0 auto' }}
          >
            <FiArrowRight />
            Démarrer ma candidature
          </Button>
        </div>
      )}

      {candidateData && (
        <>
          <ScoreOverview>
            <ScoreCard>
              <ScoreValue score={globalScore}>{globalScore}</ScoreValue>
              <ScoreLabel>/ 100</ScoreLabel>
              <ScoreProgress>
                <ScoreProgressFill score={globalScore} />
              </ScoreProgress>
              <div style={{ fontSize: theme.typography.sizes.base, fontWeight: theme.typography.weights.semibold, color: theme.colors.text, marginBottom: theme.spacing.sm }}>
                Votre score global
              </div>
              <ComparisonText>
                ℹ️ Vous êtes dans le top 35% des candidats
              </ComparisonText>
              <ViewScoringSheetButton onClick={() => navigate('/candidate/scoring-sheet')}>
                <FiFileText /> Voir ma fiche de scoring complète
              </ViewScoringSheetButton>
            </ScoreCard>

            <NextStepCard>
              <CardTitle>
                <FiTrendingUp /> Prochaines étapes
              </CardTitle>
              {loading ? (
                <div style={{ padding: theme.spacing.xl, textAlign: 'center', color: theme.colors.text + '60' }}>
                  Chargement des prochaines étapes...
                </div>
              ) : nextActions.length === 0 ? (
                <div style={{
                  padding: theme.spacing.xl,
                  textAlign: 'center',
                  background: theme.colors.success + '10',
                  borderRadius: theme.borderRadius.md,
                  border: `1px solid ${theme.colors.success}30`
                }}>
                  <FiCheckCircle style={{ fontSize: '48px', color: theme.colors.success, marginBottom: theme.spacing.md }} />
                  <div style={{ fontSize: theme.typography.sizes.base, color: theme.colors.success, fontWeight: theme.typography.weights.semibold }}>
                    Félicitations ! Votre dossier est complet
                  </div>
                  <div style={{ fontSize: theme.typography.sizes.sm, color: theme.colors.text + '70', marginTop: theme.spacing.sm }}>
                    Toutes les sections obligatoires ont été remplies
                  </div>
                </div>
              ) : (
                <NextActionsList>
                  {nextActions.map(action => (
                    <ActionItem
                      key={action.id}
                      priority={action.priority}
                      onClick={() => navigate(action.link)}
                    >
                      <ActionIcon priority={action.priority}>
                        {action.icon}
                      </ActionIcon>
                      <ActionContent>
                        <ActionTitle>{action.title}</ActionTitle>
                        <ActionDescription>{action.description}</ActionDescription>
                        <ActionImpact>📈 {action.impact}</ActionImpact>
                      </ActionContent>
                      <ActionArrow><FiChevronRight /></ActionArrow>
                    </ActionItem>
                  ))}
                </NextActionsList>
              )}
            </NextStepCard>
          </ScoreOverview>

          {household && household.withSpouse && (
            <HouseholdInfoCard>
              <HouseholdHeader>
                <FiUsers />
                <h3>Composition du foyer</h3>
              </HouseholdHeader>
              <HouseholdGrid>
                <HouseholdItem>
                  <HouseholdLabel>Demandeur principal</HouseholdLabel>
                  <HouseholdValue>
                    <FiUser />
                    {household.mainApplicant.name}
                  </HouseholdValue>
                  <div style={{ fontSize: theme.typography.sizes.sm, color: theme.colors.text + '70' }}>
                    Revenu: {household.mainApplicant.income.toLocaleString()}  FCFA/mois
                  </div>
                </HouseholdItem>
                <HouseholdItem>
                  <HouseholdLabel>Conjoint(e)</HouseholdLabel>
                  <HouseholdValue>
                    <FiHeart />
                    {household.spouse.name}
                  </HouseholdValue>
                  <div style={{ fontSize: theme.typography.sizes.sm, color: theme.colors.text + '70' }}>
                    Revenu: {household.spouse.income.toLocaleString()}  FCFA/mois
                  </div>
                </HouseholdItem>
                <HouseholdItem>
                  <HouseholdLabel>Personnes à charge</HouseholdLabel>
                  <HouseholdValue>
                    <FiUsers />
                    {household.dependents} {household.dependents > 1 ? 'enfants' : 'enfant'}
                  </HouseholdValue>
                </HouseholdItem>
                <HouseholdItem>
                  <HouseholdLabel>Revenus totaux du foyer</HouseholdLabel>
                  <HouseholdValue>
                    <FiDollarSign />
                    {household.totalIncome.toLocaleString()}  FCFA
                  </HouseholdValue>
                  <div style={{ fontSize: theme.typography.sizes.sm, color: theme.colors.text + '70' }}>
                    {household.incomePerPerson.toLocaleString()}  FCFA par personne
                  </div>
                </HouseholdItem>
              </HouseholdGrid>
              <HouseholdBadge>
                <FiCheckCircle />
                Candidature en couple marié - Score basé sur les revenus du foyer
              </HouseholdBadge>
            </HouseholdInfoCard>
          )}

          <CriteriaSection>
            <CardTitle>
              <FiBarChart2 /> Détail de votre score
            </CardTitle>
            <CriteriaGrid>
              {scoreCriteria.map((criteria, index) => (
                <CriteriaItem key={index}>
                  <CriteriaHeader>
                    <CriteriaName>
                      {getStatusIcon(criteria.status)} {criteria.name}
                    </CriteriaName>
                    <CriteriaScore status={criteria.status}>
                      {criteria.score}/{criteria.maxScore}
                    </CriteriaScore>
                  </CriteriaHeader>
                  <CriteriaBar>
                    <CriteriaBarFill
                      percentage={(criteria.score / criteria.maxScore) * 100}
                      status={criteria.status}
                    />
                  </CriteriaBar>
                  {criteria.recommendations.length > 0 && (
                    <RecommendationsList>
                      {criteria.recommendations.map((rec, i) => (
                        <RecommendationItem key={i}>{rec}</RecommendationItem>
                      ))}
                    </RecommendationsList>
                  )}
                </CriteriaItem>
              ))}
            </CriteriaGrid>
          </CriteriaSection>

          <div>
            <CardTitle style={{ marginBottom: theme.spacing.lg }}>
              <FiFileText /> Vos candidatures actives
            </CardTitle>
            <ApplicationsSection>
              {applications.map(app => (
                <ApplicationCard key={app.id} onClick={() => navigate(`/applications/${app.id}`)}>
                  <ApplicationTitle>{app.title}</ApplicationTitle>
                  <ApplicationStatus status={app.status}>
                    <FiClock /> {app.statusText}
                  </ApplicationStatus>
                </ApplicationCard>
              ))}
            </ApplicationsSection>
          </div>
        </>
      )}
    </DashboardLayout>
  );
};
